import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import {
  Rocket, MapPin, Bot, Users, ShieldCheck, Award, Handshake,
  Check, ArrowRight, Sparkles, Loader2, CreditCard,
} from "lucide-react";
import { packagesPublic } from "@/lib/api";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => {
  const t = localStorage.getItem("wt_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Slugs that expose a direct Stripe Checkout flow. SCALE + PERFORMANCE
// still route to /contact ("Talk to sales") per master plan.
const PAYABLE_SLUGS = new Set(["website-launch", "local-growth"]);

const ICONS = {
  "website-launch": Rocket,
  "local-growth": MapPin,
  "ai-business": Bot,
  "growth-partner": Users,
};
const ACCENTS = {
  "website-launch": { border: "border-sky-200", tag: "bg-sky-50 text-sky-800 border-sky-200", cta: "bg-slate-900 hover:bg-slate-800" },
  "local-growth": { border: "border-emerald-200", tag: "bg-emerald-50 text-emerald-800 border-emerald-200", cta: "bg-emerald-600 hover:bg-emerald-700" },
  "ai-business": { border: "border-amber-300", tag: "bg-amber-50 text-amber-800 border-amber-200", cta: "bg-amber-600 hover:bg-amber-700", featured: true },
  "growth-partner": { border: "border-violet-200", tag: "bg-violet-50 text-violet-800 border-violet-200", cta: "bg-violet-600 hover:bg-violet-700" },
};

// Allow the Growth Advisor widget (and any /packages?highlight=xxx deep link)
// to point at either the package `_id` (e.g. "ai-business") or its user-
// facing tier name (e.g. "SCALE"). Both resolve to the same package.
const HIGHLIGHT_ALIASES = {
  start: "website-launch",
  grow: "local-growth",
  scale: "ai-business",
  performance: "growth-partner",
};

export default function PackagesPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [params] = useSearchParams();
  const rawHighlight = (params.get("highlight") || "").toLowerCase();
  const highlightId = HIGHLIGHT_ALIASES[rawHighlight] || rawHighlight;
  const highlightIndex = useMemo(
    () => (data?.packages || []).findIndex((p) => p._id === highlightId),
    [data, highlightId]
  );
  const cardRefs = useRef({});
  const [billingMode, setBillingMode] = useState("subscription"); // subscription | one_time
  const [pricing, setPricing] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);

  usePageSeo(PAGE_SEO["/packages"]);
  useBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Pricing", url: "/pricing" }, { name: "Packages", url: "/packages" }]);

  useEffect(() => {
    packagesPublic()
      .then(setData)
      .catch(() => setErr("Could not load packages"));
    axios.get(`${API}/packages/pricing`).then((r) => setPricing(r.data))
      .catch(() => {});
    if (params.get("canceled") === "1") {
      toast.info("Checkout canceled. You can try again anytime.");
    }
  }, [params]);

  // Scroll the winning card into view once we know which one to highlight.
  useEffect(() => {
    if (!data || !highlightId) return;
    const el = cardRefs.current[highlightId];
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [data, highlightId]);

  const priceForSlug = (slug) =>
    (pricing?.packages || []).find((p) => p.slug === slug);

  const startCheckout = async (pkgSlug) => {
    setCheckingOut(pkgSlug);
    try {
      const { data: r } = await axios.post(`${API}/packages/checkout`,
        { package: pkgSlug, mode: billingMode,
          origin: window.location.origin },
        { headers: httpAuth() });
      if (r?.checkout_url) {
        window.location.href = r.checkout_url;
      } else {
        toast.error("Could not start checkout — please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed to start.");
    } finally {
      setCheckingOut(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="packages-page">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Sparkles className="h-4 w-4" /> WebsiteTech · Packages
          </div>
          <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Four outcomes. Four packages. <span className="text-orange-600">Zero lock-in.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-slate-600 sm:text-lg">
            Pick the outcome you need next. Every plan is month-to-month, every
            asset stays yours, and every managed tier includes a named human
            success manager alongside the AI.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        {err && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{err}</div>
        )}
        {/* Billing toggle — only affects START & GROW (Stripe-payable tiers) */}
        <div className="mb-8 flex items-center justify-center gap-2" data-testid="packages-billing-toggle">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Billing:</span>
          <div className="inline-flex rounded-full bg-slate-100 p-1 ring-1 ring-slate-200">
            <button
              onClick={() => setBillingMode("subscription")}
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${
                billingMode === "subscription"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-600 hover:text-slate-900"}`}
              data-testid="billing-toggle-subscription">
              Monthly subscription
            </button>
            <button
              onClick={() => setBillingMode("one_time")}
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${
                billingMode === "one_time"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-600 hover:text-slate-900"}`}
              data-testid="billing-toggle-onetime">
              One-time (3-month)
            </button>
          </div>
        </div>
        {!data ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading packages…
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {data.packages.map((p) => {
              const Icon = ICONS[p._id] || Rocket;
              const accent = ACCENTS[p._id] || ACCENTS["website-launch"];
              const isWinner = p._id === highlightId;
              return (
                <Card
                  key={p._id}
                  ref={(el) => (cardRefs.current[p._id] = el)}
                  className={`relative flex h-full flex-col border-2 bg-white p-6 transition-all ${
                    isWinner
                      ? "border-emerald-500 shadow-2xl lg:-translate-y-3 ring-4 ring-emerald-100"
                      : `${accent.border} ${accent.featured ? "shadow-xl lg:-translate-y-2" : ""}`
                  }`}
                  data-testid={`package-${p._id}`}
                >
                  {isWinner && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white shadow-lg" data-testid={`package-winner-badge-${p._id}`}>
                      ✨ Recommended for you
                    </Badge>
                  )}
                  {!isWinner && accent.featured && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white">
                      Most popular
                    </Badge>
                  )}
                  <Icon className="h-7 w-7 text-slate-700" />
                  <div className="mt-3 font-heading text-2xl font-black text-slate-900">
                    {p.name}
                  </div>
                  <Badge variant="outline" className={`mt-2 self-start text-[10px] uppercase ${accent.tag}`}>
                    {p.outcome}
                  </Badge>
                  <p className="mt-3 text-sm text-slate-600">{p.tagline}</p>
                  <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-700">
                    {(p.includes || []).map((line, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                      {PAYABLE_SLUGS.has(p._id) ? (billingMode === "subscription" ? "Monthly" : "One-time (3-month)") : "Starting price"}
                    </div>
                    <div className="mt-1 text-lg font-bold text-slate-900" data-testid={`package-price-${p._id}`}>
                      {(() => {
                        if (PAYABLE_SLUGS.has(p._id) && priceForSlug(p._id)) {
                          const pp = priceForSlug(p._id);
                          const val = billingMode === "subscription"
                            ? pp.monthly_price
                            : pp.one_time_price;
                          const suffix = billingMode === "subscription" ? "/ month" : "one-time";
                          return `$${Math.round(val).toLocaleString()} ${suffix}`;
                        }
                        return p.starting_price || "Talk to sales";
                      })()}
                    </div>
                  </div>
                  {PAYABLE_SLUGS.has(p._id) ? (
                    <Button
                      onClick={() => startCheckout(p._id)}
                      disabled={checkingOut === p._id}
                      className={`mt-4 text-white ${accent.cta}`}
                      data-testid={`package-checkout-${p._id}`}
                    >
                      {checkingOut === p._id ? (
                        <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Redirecting…</>
                      ) : (
                        <><CreditCard className="mr-1 h-4 w-4" />
                          {billingMode === "subscription" ? "Subscribe" : "Buy once"}
                          <ArrowRight className="ml-1 h-4 w-4" /></>
                      )}
                    </Button>
                  ) : (
                    <Button
                      asChild
                      className={`mt-4 text-white ${accent.cta}`}
                      data-testid={`package-cta-${p._id}`}
                    >
                      <Link to="/contact">{p.cta || "Talk to sales"} <ArrowRight className="ml-1 h-4 w-4" /></Link>
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Trust bar */}
      {data?.guarantees && (
        <section className="border-y border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-sky-50" data-testid="packages-trust">
          <div className="mx-auto grid max-w-6xl gap-6 px-4 py-12 md:grid-cols-3">
            <TrustCard icon={ShieldCheck} title="Milestone guarantee" body={data.guarantees.milestone} testid="trust-milestone" />
            <TrustCard icon={Award} title="You own everything" body={data.guarantees.ownership} testid="trust-ownership" />
            <TrustCard icon={Handshake} title="Month-to-month" body={data.guarantees.commitment} testid="trust-commitment" />
          </div>
        </section>
      )}

      {/* Compare matrix */}
      {data?.compare_matrix?.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 py-16" data-testid="packages-compare">
          <div className="mb-8 text-center">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Compare</div>
            <h2 className="mt-2 font-heading text-3xl font-black text-slate-900">Every feature. Every tier.</h2>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl mx-auto">Everything included in each plan — from launch to fully managed customer acquisition.</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-slate-700">Feature</th>
                  {data.packages.map((p, j) => {
                    const isWinner = p._id === highlightId;
                    return (
                      <th key={p._id} className={`text-center px-4 py-3 font-black uppercase tracking-wide text-xs ${isWinner ? "bg-emerald-50 text-emerald-900 border-x-2 border-emerald-500" : "text-slate-900"}`} data-testid={`compare-header-${p._id}`}>
                        {isWinner && <div className="text-[9px] font-bold text-emerald-700 mb-0.5" data-testid={`compare-header-winner-${p._id}`}>Recommended</div>}
                        {p.name}
                        <div className={`text-[10px] font-normal mt-0.5 ${isWinner ? "text-emerald-700" : "text-slate-500"}`}>{p.starting_price}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.compare_matrix.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-slate-700">{row.feature}</td>
                    {row.tiers.map((v, j) => {
                      const colWinner = highlightIndex === j;
                      return (
                        <td key={j} className={`text-center px-4 py-3 ${colWinner ? "bg-emerald-50/60 border-x-2 border-emerald-500" : ""}`}>
                          {v ? (
                            <Check className={`h-5 w-5 mx-auto ${colWinner ? "text-emerald-700" : "text-emerald-600"}`} data-testid={`compare-cell-${i}-${j}-yes`} />
                          ) : (
                            <span className="text-slate-300" data-testid={`compare-cell-${i}-${j}-no`}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-2xl font-black text-slate-900">
                Not sure which plan fits?
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Run a free Local Visibility check — we'll show you exactly which package fixes the gap.
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild className="bg-orange-600 text-white hover:bg-orange-700">
                <Link to="/local-visibility">Run free audit <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/marketing-manager">See AI Marketing Manager</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TrustCard({ icon: Icon, title, body, testid }) {
  return (
    <div className="rounded-2xl border border-white bg-white/80 p-6 shadow-sm backdrop-blur" data-testid={testid}>
      <Icon className="h-6 w-6 text-orange-600" />
      <div className="mt-3 font-heading text-lg font-bold text-slate-900">{title}</div>
      <div className="mt-2 text-sm leading-relaxed text-slate-700">{body}</div>
    </div>
  );
}
